// src/auth/auth.js
// LawAfrica frontend refinement — Backend 08BC compatibility layer.
// Registration is now a public, payment-free flow. Payment-return isolation is
// retained only for purchase/subscription payment callbacks.

const TOKEN_KEY = "token";
const AUTH_SUSPEND_KEY = "la_auth_suspended";
const AUTH_PREV_TOKEN_KEY = "la_prev_token";

const TOKEN_BLOCK_PATHS = [
  "/payments/paystack/return",
  "/paystack/return",
  "/accept-invitation",
  "/twofactor-setup",
];

function safeWindowLocation() {
  try {
    if (typeof window === "undefined") return null;
    return window.location;
  } catch {
    return null;
  }
}

function normalizePath(value) {
  return String(value || "").trim().toLowerCase();
}

function shouldBlockTokenForCurrentPage() {
  const location = safeWindowLocation();
  if (!location) return false;

  const path = normalizePath(location.pathname);
  return TOKEN_BLOCK_PATHS.some((item) => path === normalizePath(item));
}

function toBool(value) {
  if (value === true || value === false) return value;
  if (typeof value === "number") return value === 1;

  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    if (["true", "1", "yes", "y"].includes(normalized)) return true;
    if (["false", "0", "no", "n"].includes(normalized)) return false;
  }

  return false;
}

export function saveToken(token) {
  if (!token) return;
  localStorage.setItem(TOKEN_KEY, token);
}

export function getToken() {
  try {
    if (localStorage.getItem(AUTH_SUSPEND_KEY) === "1") return null;
    if (shouldBlockTokenForCurrentPage()) return null;
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

export function clearToken() {
  try {
    localStorage.removeItem(TOKEN_KEY);
  } catch {
    // Browser storage can be unavailable in restrictive/privacy contexts.
  }
}

export function logout() {
  clearToken();
}

export function isAuthed() {
  return !!getToken();
}

// Kept for backwards compatibility with older payment components. Registration
// no longer needs these helpers, but existing purchase/subscription code may.
export function suspendAuth() {
  try {
    const token = localStorage.getItem(TOKEN_KEY);
    if (token) localStorage.setItem(AUTH_PREV_TOKEN_KEY, token);
    localStorage.setItem(AUTH_SUSPEND_KEY, "1");
    localStorage.removeItem(TOKEN_KEY);
  } catch {
    // Ignore storage failures and let the active request surface any auth issue.
  }
}

export function resumeAuth() {
  try {
    const previous = localStorage.getItem(AUTH_PREV_TOKEN_KEY);
    if (previous) localStorage.setItem(TOKEN_KEY, previous);
    localStorage.removeItem(AUTH_PREV_TOKEN_KEY);
    localStorage.removeItem(AUTH_SUSPEND_KEY);
  } catch {
    // Ignore storage failures.
  }
}

export function isAuthSuspended() {
  try {
    return localStorage.getItem(AUTH_SUSPEND_KEY) === "1";
  } catch {
    return false;
  }
}

export function decodeJwt(token) {
  try {
    const parts = String(token || "").split(".");
    if (parts.length !== 3) return null;

    const payload = parts[1];
    const base64 = payload.replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64 + "===".slice((base64.length + 3) % 4);
    const json = atob(padded);
    return JSON.parse(json);
  } catch {
    return null;
  }
}

export function isTokenExpired() {
  const token = getToken();
  if (!token) return true;

  const payload = decodeJwt(token);
  if (!payload?.exp) return false;

  return Number(payload.exp) <= Math.floor(Date.now() / 1000);
}

function firstClaim(payload, keys) {
  for (const key of keys) {
    const value = payload?.[key];
    if (value !== undefined && value !== null && value !== "") return value;
  }
  return null;
}

export function getAuthClaims() {
  const token = getToken();
  const payload = token ? decodeJwt(token) : null;
  if (!payload) return null;

  const role = firstClaim(payload, [
    "role",
    "Role",
    "http://schemas.microsoft.com/ws/2008/06/identity/claims/role",
  ]);

  const institutionIdRaw = firstClaim(payload, ["institutionId", "InstitutionId"]);
  const institutionId =
    institutionIdRaw == null || institutionIdRaw === ""
      ? null
      : Number(institutionIdRaw);

  const userIdRaw = firstClaim(payload, [
    "userId",
    "UserId",
    "sub",
    "http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier",
    "http://schemas.microsoft.com/ws/2008/06/identity/claims/nameidentifier",
  ]);

  const userId = userIdRaw == null || userIdRaw === "" ? null : Number(userIdRaw);

  const isGlobalAdmin = toBool(
    firstClaim(payload, ["isGlobalAdmin", "IsGlobalAdmin"]),
  );

  const isInstitutionAdmin = toBool(
    firstClaim(payload, ["isInstitutionAdmin", "IsInstitutionAdmin"]),
  );

  const institutionRole = firstClaim(payload, ["institutionRole", "InstitutionRole"]);
  const userType = firstClaim(payload, ["userType", "UserType"]);

  return {
    payload,
    role,
    userType,
    institutionId: Number.isFinite(institutionId) ? institutionId : null,
    userId: Number.isFinite(userId) ? userId : null,
    isGlobalAdmin,
    isInstitutionAdmin,
    institutionRole,
  };
}

export function isAdminRole() {
  const claims = getAuthClaims();
  if (!claims) return false;

  const role = String(claims.role || "").toLowerCase();
  return role === "admin" || role === "globaladmin" || claims.isGlobalAdmin;
}

export function isGlobalAdmin() {
  return !!getAuthClaims()?.isGlobalAdmin;
}

export function isInstitutionAdminWithInstitution() {
  const claims = getAuthClaims();
  if (!claims) return false;

  const role = String(claims.role || "").toLowerCase();
  const institutionRole = String(claims.institutionRole || "").toLowerCase();

  const institutionAdmin =
    role === "institutionadmin" ||
    institutionRole === "institutionadmin" ||
    claims.isInstitutionAdmin;

  return !!(institutionAdmin && claims.institutionId && claims.institutionId > 0);
}

export function canSeeApprovals() {
  return isAdminRole() || isInstitutionAdminWithInstitution();
}
